import type { JSX } from "solid-js";
import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
} from "solid-js";

import { cn } from "@/lib/tailwindcssTool";
import Tooltip from "@corvu/tooltip";

export type TooltipPlacement =
  | "top-start"
  | "top"
  | "top-end"
  | "right-start"
  | "right"
  | "right-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end"
  | "left-start"
  | "left"
  | "left-end";

export type TooltipTrigger = "hover" | "click" | "both";

type TooltipOffset = number | { x?: number; y?: number };

interface EnhancedTooltipProps {
  /** 外部class */
  class?: string;
  /** 触发元素 */
  children: JSX.Element;
  /** 显示内容 */
  content: string | JSX.Element;
  /** 位置 */
  // 默认 bottom
  placement?: TooltipPlacement;
  /** 触发方式 */
  // 默认 hover
  trigger?: TooltipTrigger;
  /** 打开延迟 */
  // 默认 100ms
  openDelay?: number;
  /** 显示持续时间，0 表示不自动关闭 */
  // Todo 目前有问题, 需要在调适
  duration?: number;
  /** 内容样式 */
  // 默认 text-general-light bg-general-bg4 relative z-[9999] rounded-[6px] px-3 py-2 text-[12px] font-[600] leading-[20px] shadow-lg
  contentClass?: string;
  /** 箭头样式 */
  // 默认 w-2 h-2
  arrowClass?: string;
  /** 与触发元素的距离 */
  // 默认 { y: 3 }
  offset?: TooltipOffset;
  /** 是否显示箭头 */
  // 默认 true
  showArrow?: boolean;
  /** 箭头颜色 */
  // 预设 text-general-bg4
  arrowColor?: `text-${string}`;
  /** 是否阻止事件冒泡 */
  // 默认 true
  stopPropagation?: boolean;
}

const getArrowStyle = (placement: TooltipPlacement) => {
  const baseStyle = "w-2 h-2";

  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
      return `${baseStyle} rotate-180`;
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      return baseStyle;
    case "left":
    case "left-start":
    case "left-end":
      return `${baseStyle} rotate-90`;
    case "right":
    case "right-start":
    case "right-end":
      return `${baseStyle} rotate-0`;
    default:
      return baseStyle;
  }
};

const calculateOffset = (
  offset: TooltipOffset | undefined,
  placement: TooltipPlacement,
  showArrow: boolean,
) => {
  const arrowSize = showArrow ? 6 : 0;
  const defaultOffset = { x: 0, y: 0 };

  if (typeof offset === "number") {
    return offset + arrowSize;
  }

  if (typeof offset === "object") {
    const { x = defaultOffset.x, y = defaultOffset.y } = offset;

    switch (placement) {
      case "top":
      case "top-start":
      case "top-end":
        return { mainAxis: y + arrowSize, crossAxis: x };
      case "bottom":
      case "bottom-start":
      case "bottom-end":
        return { mainAxis: y + arrowSize, crossAxis: x };
      case "left":
      case "left-start":
      case "left-end":
        return { mainAxis: x + arrowSize, crossAxis: y };
      case "right":
      case "right-start":
      case "right-end":
        return { mainAxis: x + arrowSize, crossAxis: y };
      default:
        return { mainAxis: y + arrowSize, crossAxis: x };
    }
  }

  return defaultOffset.y + arrowSize;
};

export const Tooltips = (props: EnhancedTooltipProps) => {
  const [isOpen, setIsOpen] = createSignal(false);
  const [triggerElement, setTriggerElement] = createSignal<HTMLElement>();

  let openTimer: NodeJS.Timeout;
  let closeTimer: NodeJS.Timeout;
  let clickOutsideHandler: (e: MouseEvent) => void;

  const placement = () => props.placement || "bottom";
  const trigger = () => props.trigger || "hover";
  const openDelay = () => props.openDelay ?? 100;
  const duration = () => props.duration ?? 0; // 0 表示不自动关闭
  const showArrow = () => props.showArrow !== false;

  const clearTimers = () => {
    if (openTimer) clearTimeout(openTimer);
    if (closeTimer) clearTimeout(closeTimer);
  };

  const showTooltip = () => {
    clearTimers();
    openTimer = setTimeout(() => {
      setIsOpen(true);
      if (duration() > 0) {
        closeTimer = setTimeout(() => setIsOpen(false), duration());
      }
    }, openDelay());
  };

  const hideTooltip = () => {
    clearTimers();
    setIsOpen(false);
  };

  const handleClickOutside = (e: MouseEvent) => {
    const triggerEl = triggerElement();
    if (triggerEl && !triggerEl.contains(e.target as Node)) {
      hideTooltip();
    }
  };

  const handleMouseEnter = () => {
    if (trigger() === "hover" || trigger() === "both") showTooltip();
  };

  const handleMouseLeave = () => {
    if (trigger() === "hover" || trigger() === "both") hideTooltip();
  };

  const handleClick = (e: MouseEvent) => {
    if (props.stopPropagation !== false) {
      e.stopPropagation();
    }
    if (trigger() === "click" || trigger() === "both") {
      if (isOpen()) {
        hideTooltip();
      } else {
        showTooltip();
      }
    }
  };

  createEffect(
    on(
      createMemo(
        () => isOpen() && (trigger() === "click" || trigger() === "both"),
      ),
      (boolean) => {
        if (boolean) {
          clickOutsideHandler = handleClickOutside;
          document.addEventListener("click", clickOutsideHandler);
        } else if (clickOutsideHandler) {
          document.removeEventListener("click", clickOutsideHandler);
        }
      },
    ),
  );

  onCleanup(() => {
    clearTimers();
    if (clickOutsideHandler) {
      document.removeEventListener("click", clickOutsideHandler);
    }
  });

  return (
    <Tooltip
      open={isOpen()}
      onOpenChange={setIsOpen}
      placement={placement()}
      floatingOptions={{
        offset: calculateOffset(
          props.offset || { y: 0, x: 0 },
          placement(),
          showArrow(),
        ),
        flip: true,
        shift: true,
      }}
    >
      <Tooltip.Trigger
        as="div"
        class={cn("inline-block", props.class)}
        ref={setTriggerElement}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {props.children}
      </Tooltip.Trigger>

      <Tooltip.Portal>
        <Tooltip.Content
          class={cn(
            "data-open:animate-in data-open:fade-in-50% data-open:slide-in-from-bottom-1 data-closed:animate-out data-closed:fade-out-50% data-closed:slide-out-to-bottom-1 text-general-light bg-general-bg4 relative z-[9999] rounded-[6px] px-3 py-2 text-[12px] font-[600] leading-[20px] shadow-lg",
            props.contentClass,
          )}
        >
          {props.content}
          {showArrow() && (
            <Tooltip.Arrow
              class={`${props.arrowColor || "text-general-bg4"} ${props.arrowClass || ""} ${getArrowStyle(placement())}`}
            />
          )}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  );
};
